import { useTranslation } from 'helpers/hooks/useTranslation'
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled, { css } from 'styled-components'
import { Icon, IconSize } from 'uiComponents/atoms/Icon/Icon'
import { IconName } from 'uiComponents/atoms/Icon/IconName'
import NumberBadge from 'uiComponents/atoms/NumberBadge/NumberBadge'
import {
  bordersColor,
  complementaryColor,
  outlineGrey,
  primaryColor,
  textBlack,
} from 'uiComponents/constants/colors'

interface StyledContentProps {
  styledContentStyles?:
    | {
        padding: string | null | undefined
      }
    | undefined
}

const DROPDOWN_CONTENT_WIDTH = 368

enum UnfoldingFilterBoxContentAlignment {
  LEFT = 'Left',
  RIGHT = 'Right',
}

const Wrapper = styled.div`
  display: inline-block;
  position: relative;
`

const StyledDropdownButton = styled.button<{
  isActive?: boolean
  isOpen: boolean
}>`
  display: inline-flex;
  align-items: center;
  border: ${props =>
    props.isActive ? `1px solid ${primaryColor}` : `1px solid ${outlineGrey}`};
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  background-color: ${complementaryColor};
  color: ${textBlack};
`

const StyledDropdownContentWrapper = styled.div<{
  contentAlignment: UnfoldingFilterBoxContentAlignment
}>`
  display: flex;
  flex-direction: column;
  position: absolute;
  z-index: 1;
  border-radius: 3px;
  box-shadow: 0 10px 15px 0 rgba(0, 0, 0, 0.08), 0 2px 2px 0 rgba(0, 0, 0, 0.05),
    0 -2px 2px 0 rgba(0, 0, 0, 0.02);
  background-color: white;
  margin-top: 5px;
  word-break: break-word;
  width: ${DROPDOWN_CONTENT_WIDTH}px;
  z-index: 1000;
  left: ${props =>
    props.contentAlignment === UnfoldingFilterBoxContentAlignment.LEFT
      ? 0
      : 'unset'};
  right: ${props =>
    props.contentAlignment === UnfoldingFilterBoxContentAlignment.RIGHT
      ? 0
      : 'unset'};
`
const StyledContent = styled.div<StyledContentProps>`
  ${({ styledContentStyles }) =>
    styledContentStyles &&
    css`
      padding: ${styledContentStyles.padding};
    `};
`

const StyledIcon = styled(Icon)`
  height: 6px;
  width: 6px;
  flex: 1;
  margin-left: 10px;
`

const StyledNumberBadge = styled(NumberBadge)`
  height: 18px;
  padding: 0 8px;
  font-size: 10px;
  line-height: 14px;
  margin-left: 4px;
`

const ResetWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid ${bordersColor};
`

const StyledResetButton = styled.button`
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 18px;
  color: ${primaryColor};
  padding: 16px;
`

const SettingsWrapper = styled.ul`
  z-index: 0;
`

const SettingItem = styled.li`
  border-top: 1px solid ${bordersColor};
  list-style: none;
`

interface Props {
  label: string
  dataTestId: string
  activeFiltersCount: number
  onReset: () => void
  settings?: ReactNode[]
  styledContentStyles?: { padding: string | null | undefined }
  onOpen: () => void
  onClose: () => void
}

const getIconNameForCurrentState = (isOpen: boolean): IconName =>
  isOpen ? IconName.IcDropdownOpenTintable : IconName.IcDropdownClosedTintable

const getContentAlignment = (
  dropdownButtonRef: React.RefObject<HTMLButtonElement | null>,
) => {
  const contentSize = dropdownButtonRef.current?.getBoundingClientRect()
  const doesContentFitIntoViewport =
    contentSize && contentSize.x + DROPDOWN_CONTENT_WIDTH < window.innerWidth

  return doesContentFitIntoViewport
    ? UnfoldingFilterBoxContentAlignment.LEFT
    : UnfoldingFilterBoxContentAlignment.RIGHT
}

const UnfoldingFilterBox: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  label,
  dataTestId,
  activeFiltersCount,
  onReset,
  settings,
  styledContentStyles,
  onOpen,
  onClose,
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const outerContainerRef = useRef<HTMLDivElement>(null)
  const dropdownButtonRef = useRef<HTMLButtonElement>(null)

  const handleClick = useCallback(() => {
    if (isOpen) {
      onClose()
    } else {
      onOpen()
    }
    setIsOpen(!isOpen)
  }, [isOpen])

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (isOpen) {
        if (event.keyCode === 27) {
          onClose()
          setIsOpen(false)
        }
      }
    },
    [isOpen],
  )

  const onMouseDown = useCallback(
    (event: MouseEvent) => {
      if (isOpen && event.target) {
        if (outerContainerRef?.current?.contains(event?.target as Node)) {
          return
        }

        setTimeout(() => {
          onClose()
          setIsOpen(false)
        })
      }
    },
    [isOpen],
  )

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onMouseDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [onKeyDown, onMouseDown])

  const contentAlignment = getContentAlignment(dropdownButtonRef)

  const customDataTestId = `FilterAccordion_${
    dataTestId ? dataTestId : 'default'
  }${isOpen && '_expanded'}`

  const uniqueAriaLabelledById = `unfolding_filter_box_id_${label}`

  const isActiveState = activeFiltersCount > 0

  return (
    <Wrapper ref={outerContainerRef} data-testid={customDataTestId}>
      <StyledDropdownButton
        onClick={handleClick}
        isActive={isActiveState}
        isOpen={isOpen}
        id={uniqueAriaLabelledById}
        aria-haspopup={isOpen}
        aria-expanded={isOpen}
        ref={dropdownButtonRef}
      >
        {label}
        {isActiveState && (
          <StyledNumberBadge data-testid="Active_Filter_Count">
            {activeFiltersCount}
          </StyledNumberBadge>
        )}
        <StyledIcon
          size={IconSize.XXS}
          asset={getIconNameForCurrentState(isOpen)}
          data-testid={`Toggle_Icon_${isOpen ? 'Open' : 'Closed'}`}
        />
      </StyledDropdownButton>
      {isOpen && (
        <StyledDropdownContentWrapper
          aria-labelledby={uniqueAriaLabelledById}
          contentAlignment={contentAlignment}
        >
          <StyledContent styledContentStyles={styledContentStyles}>
            {children}
          </StyledContent>
          {settings && Boolean(settings.length) && (
            <SettingsWrapper>
              {settings.map((setting, index) => (
                <SettingItem key={index}>{setting}</SettingItem>
              ))}
            </SettingsWrapper>
          )}
          <ResetWrapper>
            <StyledResetButton
              onClick={onReset}
              data-testid="Accordion_ClearButton"
            >
              <Icon size={IconSize.S} asset={IconName.IcReset} />
              {t('Zurücksetzen')}
            </StyledResetButton>
          </ResetWrapper>
        </StyledDropdownContentWrapper>
      )}
    </Wrapper>
  )
}

export default UnfoldingFilterBox
